import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/logo';

type State = { error: Error | null };

/**
 * Last resort for a render-time crash. React has no hook equivalent, so this
 * stays a class component.
 *
 * Reload rather than "try again": the component tree that threw is the one that
 * would re-render, and a fresh load is far more likely to recover.
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('unhandled_render_error', error, info.componentStack);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 px-4 text-center">
        <Logo />

        <div>
          <p className="text-sm font-medium text-ink">Something broke</p>
          <p className="mt-1 max-w-sm text-sm text-ink-muted">
            This is a bug on our side, not something you did. Reloading usually
            clears it.
          </p>
        </div>

        <Button onClick={() => window.location.reload()}>Reload</Button>
      </div>
    );
  }
}
